/* eslint-disable no-async-promise-executor */

import fs = require("fs");
import path = require("path");
import { EventEmitter } from "events";
import crypto = require("crypto");
import util = require("util");

import Endpoints = require("./Endpoints");
const { version } = JSON.parse(fs.readFileSync(path.join(__dirname, "../package.json"), { encoding: "utf8" }));
import Constants = require("./Constants");

export type HTTPMethod = "get" | "post" | "patch" | "head" | "put" | "delete" | "connect" | "options" | "trace";

export type RESTPostAPIAttachmentsRefreshURLsResult = {
	refreshed_urls: Array<{
		original: string;
		refreshed: string;
	}>;
}

export type RatelimitInfo = {
	message: string;
	retry_after: number;
	global: boolean;
	code?: number;
}





export interface Queue {



	calls: Array<() => any>;



	running: boolean;



	blocked: boolean;







	enqueue<T>(fn: () => T): Promise<T>;





	setBlocked(blocked: boolean): void;




	drop(): void;
}


const routeRegex = /\/([a-z-]+)\/(?:\d+)/g;
const reactionsRegex = /\/reactions\/[^/]+/g;
const reactionsUserRegex = /\/reactions\/:id\/[^/]+/g;
const webhooksRegex = /^\/webhooks\/(\d+)\/[A-Za-z0-9-_]{64,}/;
const isMessageEndpointRegex = /\/messages\/:id$/;
const isGuildChannelsRegex = /\/guilds\/\d+\/channels$/;

const disallowedBodyMethods = new Set(["head", "get", "delete"]);




export class DiscordAPIError extends Error {
	public method: string;
	public path: string;
	public code: number;
	public httpStatus: number;
	public request: RequestEventData;
	public response: Response;

	public constructor(error: { message?: string; code?: number; }, request: RequestEventData, response: Response) {
		super();
		this.name = "DiscordAPIError";
		this.message = error.message ?? util.inspect(error);
		this.method = request.method;
		this.path = request.endpoint;
		this.code = error.code ?? 4000;
		this.httpStatus = response.status;

		Object.defineProperties(this, {
			request: { enumerable: false, value: request },
			response: { enumerable: false, value: response },
		});
	}
}






export class AsyncSequentialQueue implements Queue {
	public calls: Array<() => any> = [];

	private _blocked = false;
	private _running = false;

	public get blocked(): boolean {
		return this._blocked;
	}

	public get running(): boolean {
		return this._running;
	}

	public enqueue<T>(fn: () => T): Promise<T> {
		return new Promise((resolve, reject) => {
			const wrapped = async () => {
				try {
					resolve(await fn());
				} catch (e) {
					reject(e as Error);
				}
			}

			this.calls.push(wrapped);
			this._tryRun();
		});
	}

	public setBlocked(blocked: boolean): void {
		this._blocked = blocked;
		this._tryRun();
	}

	public drop(): void {
		this.calls.length = 0;
		this._running = false;
	}

	private _tryRun(): void {
		if (this._blocked || this._running) return;
		this._running = true;
		this._next();
	}

	private async _next(): Promise<void> {
		if (this._blocked || !this._running) return
		const cb = this.calls.shift();
		await cb?.();
		if (this.calls.length && !this._blocked) setImmediate(() => this._next());
		else this._running = false;
	}
}






export class Ratelimiter<B extends typeof GlobalBucket = typeof GlobalBucket> {



	public buckets = new Map<string, InstanceType<B>>();



	public globalBucket = new LocalBucket(Constants.GLOBAL_REQUESTS_PER_SECOND, 1000);




	public get global() {
		return this.globalBucket.remaining === 0;
	}





	public constructor(public BucketConstructor: B = GlobalBucket as B) {}









	public routify(url: string, method: string): string {
		let route = url.replace(routeRegex, function (match, p: string) {
			return p === "channels" || p === "guilds" || p === "webhooks" ? match : `/${p}/:id`;
		}).replace(reactionsRegex, "/reactions/:id").replace(reactionsUserRegex, "/reactions/:id/:userId").replace(webhooksRegex, "/webhooks/$1/:token");

		if (method === "DELETE" && isMessageEndpointRegex.test(route)) route = method + route;
		else if (method === "GET" && isGuildChannelsRegex.test(route)) route = "/guilds/:id/channels";

		if (method === "PUT" || method === "DELETE") {
			const index = route.indexOf("/reactions");
			if (index !== -1) route = "MODIFY" + route.slice(0, index + 10);
		}

		return route;
	}








	public queue<T>(fn: (bucket: InstanceType<B>) => T, url: string, method: string): Promise<T> {
		const routeKey = this.routify(url, method);

		let bucket = this.buckets.get(routeKey);
		if (!bucket) {
			bucket = new this.BucketConstructor(this, routeKey) as InstanceType<B>;
			this.buckets.set(routeKey, bucket);
		}

		return new Promise<T>((resolve, reject) => {
			this.globalBucket.enqueue(() => {
				bucket.enqueue(fn).then(resolve).catch(reject);
			});
		});
	}





	public setGlobal(ms: number): void {
		this.globalBucket.remaining = 0;
		this.globalBucket.queue.setBlocked(true);
		this.globalBucket.makeResetTimeout(ms);
	}
}






export class LocalBucket {



	public queue: Queue = new AsyncSequentialQueue();



	public remaining: number;

	private resetTimeout: NodeJS.Timeout | null = null;







	public constructor(public limit = 5, public reset = 5000, remaining?: number) {
		this.remaining = remaining ?? limit;
	}







	public enqueue<T>(fn: (bkt: this) => T): Promise<T> {
		return this.queue.enqueue<T>(() => {
			this.remaining--;
			if (this.remaining === 0) this.queue.setBlocked(true);
			if (!this.resetTimeout) this.makeResetTimeout(this.reset);

			return fn(this);
		});
	}






	public makeResetTimeout(ms: number): void {
		if (this.resetTimeout) clearTimeout(this.resetTimeout);
		this.resetTimeout = setTimeout(() => this.resetRemaining(), ms).unref();
	}





	public resetRemaining(): void {
		if (this.resetTimeout) clearTimeout(this.resetTimeout);
		this.resetTimeout = null;

		this.remaining = this.limit;
		this.queue.setBlocked(false);
	}





	public dropQueue(): void {
		this.queue.drop();
	}
}






export class GlobalBucket extends LocalBucket {





	public constructor(public readonly ratelimiter: Ratelimiter, public readonly routeKey: string, limit = 5, reset = 5000, remaining?: number) {
		super(limit, reset, remaining);
	}





	public resetRemaining(): void {
		if (!this.queue.calls.length) this.ratelimiter.buckets.delete(this.routeKey);
		super.resetRemaining();
	}
}

export type RequestEventData = {
	endpoint: string;
	method: string;
	dataType: "json" | "multipart";
	data: any;
};

export type HandlerEvents = {
	request: [string, RequestEventData];
	done: [string, Response, RequestEventData];
	requestError: [string, Error];
	rateLimit: [{ timeout: number; remaining: number; limit: number; method: string; path: string; route: string; }];
}





export class RequestHandler extends EventEmitter<HandlerEvents> {
	public options: {

		baseHost: string;

		baseURL: string;

		bypassBuckets: boolean;

		retryFailed: boolean;

		retryLimit: number;
		headers: { Authorization?: string; "User-Agent": string; }
	};
	public latency: number;
	public apiURL: string;






	public constructor(public ratelimiter: Ratelimiter, options?: { token?: string; } & Partial<Omit<RequestHandler["options"], "headers">>) {
		super();

		this.options = {
			baseHost: options?.baseHost ?? Endpoints.BASE_HOST,
			baseURL: options?.baseURL ?? Endpoints.BASE_URL,
			bypassBuckets: options?.bypassBuckets ?? false,
			retryFailed: options?.retryFailed ?? false,
			retryLimit: options?.retryLimit ?? Constants.DEFAULT_RETRY_LIMIT,
			headers: {
				"User-Agent": `Discordbot (https:
			}
		};
		if (options?.token) this.options.headers.Authorization = options.token;

		this.apiURL = this.options.baseHost + Endpoints.BASE_URL;
		this.latency = 500;
	}











	public request(endpoint: string, params: Record<string, any> | undefined, method: HTTPMethod, dataType: "json", data?: any, extraHeaders?: Record<string, string>, retries?: number): Promise<any>
	public request(endpoint: string, params: Record<string, any> | undefined, method: HTTPMethod, dataType: "multipart", data?: FormData, extraHeaders?: Record<string, string>, retries?: number): Promise<any>
	public request(endpoint: string, params: Record<string, any> = {}, method: HTTPMethod, dataType: "json" | "multipart", data?: any, extraHeaders?: Record<string, string>, retries = this.options.retryLimit): Promise<any> {
		const stack = new Error().stack as string;
		return new Promise(async (resolve, reject) => {
			const fn = async (bkt?: GlobalBucket | undefined) => {
				const reqId = crypto.randomBytes(20).toString("hex");
				try {
					const request = { endpoint, method: method.toUpperCase(), dataType, data: data ?? {} };
					this.emit("request", reqId, request);

					const before = Date.now();

					let response: Response;
					switch (dataType) {
					case "json":
						response = await this._request(endpoint, params, method, data, extraHeaders);
						break;
					case "multipart":
						if (!data) throw new Error("No multipart data");
						response = await this._multiPartRequest(endpoint, params, method, data, extraHeaders);
						break;
					default:
						throw new Error("Forbidden dataType. Use json or multipart or ensure multipart has FormData");
					}

					this.latency = Date.now() - before;

					if (bkt) this._applyRatelimitHeaders(bkt, response.headers);

					if (response.status && !Constants.OK_STATUS_CODES.has(response.status) && response.status !== 429) {
						if (this.options.retryFailed && !Constants.DO_NOT_RETRY_STATUS_CODES.has(response.status) && retries !== 0) return this.request(endpoint, params, method, dataType as any, data, extraHeaders, retries--).then(resolve).catch(reject);
						throw new DiscordAPIError({ message: await response.text() }, request, response);
					}

					if (response.status === 429) {
						const b = await response.json() as RatelimitInfo;
						if (b.global) this.ratelimiter.setGlobal(b.retry_after * 1000);
						else bkt?.queue.setBlocked(true);

						this.emit("rateLimit", {
							timeout: bkt?.reset ?? b.retry_after * 1000,
							remaining: 0,
							limit: bkt?.limit ?? Infinity,
							method: method.toUpperCase(),
							path: endpoint,
							route: bkt?.routeKey ?? this.ratelimiter.routify(endpoint, method.toUpperCase())
						});

						throw new DiscordAPIError({ message: b.message, code: b.code ?? 429 }, request, response);
					}

					this.emit("done", reqId, response, request);

					if (response.body) {
						let b: any;
						try {
							b = await response.json();
						} catch {
							return resolve(undefined);
						}
						return resolve(b);
					} else return resolve(undefined);
				} catch (error) {
					if (error?.stack) error.stack = error.stack + `\n${stack.split("\n").slice(1).join("\n")}`;
					this.emit("requestError", reqId, error);
					return reject(error as Error);
				}
			};

			if (this.options.bypassBuckets) fn();
			else this.ratelimiter.queue(fn, endpoint, method.toUpperCase());
		});
	}







	private _applyRatelimitHeaders(bkt: GlobalBucket, headers: Headers): void {
		const remaining = headers.get("x-ratelimit-remaining");
		const limit = headers.get("x-ratelimit-limit");
		const resetAfter = headers.get("x-ratelimit-reset-after");

		if (remaining) bkt.remaining = parseInt(remaining);
		if (limit) bkt.limit = parseInt(limit);
		if (resetAfter) {
			bkt.reset = parseFloat(resetAfter) * 1000;
			bkt.makeResetTimeout(bkt.reset);
		}
	}









	private async _request(endpoint: string, params: Record<string, any> = {}, method: HTTPMethod, data?: any, extraHeaders?: Record<string, string>): Promise<Response> {
		const headers = { ...this.options.headers, ...extraHeaders };
		if (typeof data !== "string" && data?.reason) {
			headers["X-Audit-Log-Reason"] = encodeURIComponent(data.reason);
			delete data.reason;
		}

		let body: string | undefined = undefined;
		if (!disallowedBodyMethods.has(method)) {
			if (typeof data === "object") body = JSON.stringify(data);
			else body = String(data);

			headers["Content-Type"] = "application/json";
		}

		return fetch(`${this.apiURL}${endpoint}${appendQuery(params)}`, {
			method: method.toUpperCase(),
			headers,
			body
		});
	}










	private async _multiPartRequest(endpoint: string, params: Record<string, any> = {}, method: HTTPMethod, data: FormData, extraHeaders?: Record<string, string>): Promise<Response> {
		const headers = { ...this.options.headers, ...extraHeaders };

		return fetch(`${this.apiURL}${endpoint}${appendQuery(params)}`, {
			method: method.toUpperCase(),
			headers,
			body: data
		});
	}
}

function appendQuery(query: Record<string, any>): string {
	let count = 0;
	for (const [key, value] of Object.entries(query)) {
		if (value == undefined) delete query[key];
		else count++;
	}

	return count > 0 ? `?${new URLSearchParams(query).toString()}` : "";
}
